import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Play, Users, Video, Image, Coins, Shield, 
  Sparkles, TrendingUp, Heart, MessageCircle,
  ChevronRight, Star, Globe, Zap, Award
} from 'lucide-react';
import { t } from '../lib/i18n';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  
  // Get language from localStorage or default to 'en'
  const language = localStorage.getItem('fityh_lang') || 'en';
  const tr = t(language);
  
  // Feature cards data with translations
  const features = [
    {
      icon: Video,
      title: tr.liveStreaming,
      description: tr.liveStreamingDesc,
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: Sparkles,
      title: tr.aiCreatorStudio,
      description: tr.aiCreatorStudioDesc,
      color: "from-violet-500 to-purple-500"
    },
    {
      icon: Coins,
      title: tr.creatorEconomy,
      description: tr.creatorEconomyDesc,
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: Shield,
      title: tr.safeAndSecure,
      description: tr.safeAndSecureDesc,
      color: "from-emerald-500 to-teal-500"
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden" data-testid="landing-page">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-[128px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/20 rounded-full blur-[128px] animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-500/10 rounded-full blur-[100px] animate-pulse delay-500" />
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0a0a0f]/90 backdrop-blur-xl border-b border-white/10' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="FityH" className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl" />
              <div>
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
                  FityH
                </span>
                <p className="text-[10px] sm:text-xs text-white/50 hidden sm:block">Connect. Share. Create. Earn.</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full px-3 sm:px-6"
                onClick={() => navigate('/login')}
              >
                {tr.login}
              </Button>
              <Button
                className="bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 text-white rounded-full px-4 sm:px-8 font-semibold shadow-lg shadow-pink-500/25"
                onClick={() => navigate('/register')}
                data-testid="get-started-btn"
              >
                {tr.register}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6 sm:mb-8">
            <Zap className="w-4 h-4 text-pink-500" />
            <span className="text-sm text-white/80">{tr.theFutureOfSocial}</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6 sm:mb-8">
            <span className="block">{tr.whereCreators}</span>
            <span className="bg-gradient-to-r from-pink-500 via-violet-500 to-blue-500 bg-clip-text text-transparent">
              {tr.thriveAndEarn}
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl lg:text-2xl text-white/60 max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed">
            {tr.landingSubheadline}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 sm:mb-16">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 text-white rounded-full px-8 sm:px-12 py-6 text-lg font-semibold shadow-xl shadow-pink-500/30 transition-all hover:scale-105"
              onClick={() => navigate('/register')}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {tr.startCreatingFree}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 rounded-full px-8 sm:px-12 py-6 text-lg"
              onClick={() => navigate('/explore')}
            >
              <Play className="w-5 h-5 mr-2" />
              {tr.exploreContent}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-20 sm:py-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold mb-4">
              {tr.everythingYouNeed}
              <span className="bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent"> {tr.succeed}</span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              {tr.powerfulTools}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="relative py-20 sm:py-32 px-4 bg-gradient-to-b from-transparent via-pink-500/5 to-transparent">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8">
            <Star className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-white/80">{tr.lovedByCreators}</span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-bold mb-6">
            {tr.joinTheRevolution}
            <span className="bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent"> {tr.revolution}</span>
          </h2>

          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-12">
            {tr.joinRevolutionDesc}
          </p>

          {/* Feature List */}
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <div className="flex items-center gap-3 justify-center p-4 rounded-xl bg-white/5">
              <Globe className="w-5 h-5 text-pink-500" />
              <span className="text-white/80">{tr.globalCommunity}</span>
            </div>
            <div className="flex items-center gap-3 justify-center p-4 rounded-xl bg-white/5">
              <Award className="w-5 h-5 text-violet-500" />
              <span className="text-white/80">{tr.creatorRewards}</span>
            </div>
            <div className="flex items-center gap-3 justify-center p-4 rounded-xl bg-white/5">
              <Heart className="w-5 h-5 text-rose-500" />
              <span className="text-white/80">{tr.supportiveCulture}</span>
            </div>
          </div>

          <Button
            size="lg"
            className="bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 text-white rounded-full px-12 py-6 text-lg font-semibold shadow-xl shadow-pink-500/30 transition-all hover:scale-105"
            onClick={() => navigate('/register')}
          >
            {tr.createYourAccount}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="FityH" className="w-10 h-10 rounded-xl" />
              <span className="text-xl font-bold bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
                FityH
              </span>
            </div>

            {/* Links */}
            <div className="flex items-center gap-6 text-sm text-white/60">
              <Link to="/privacy" className="hover:text-white transition-colors">{tr.privacy}</Link>
              <Link to="/rules" className="hover:text-white transition-colors">{tr.rules}</Link>
              <a href="mailto:fityhapp@gmail.com" className="hover:text-white transition-colors">{tr.contactUs}</a>
            </div>

            {/* Copyright */}
            <p className="text-sm text-white/40">
              © 2026 FityH. {tr.allRightsReserved}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
